import { el, qs } from '../utils/dom.js';

/**
 * Multi-layer custom palettes — named palettes that paint across all layers
 * + properties + objects in one stroke. TileForge differentiator.
 *
 * Palette entry:
 * {
 *   name: 'Grass Path Edge',
 *   layers: {
 *     'Ground': tileId,
 *     'Objects': tileId,
 *     'Overhead': tileId,
 *   },
 *   properties: {
 *     collision: false,
 *     occlusion: false,
 *   },
 *   object: null, // optional object to spawn
 * }
 */
export class CustomPalettes {
  constructor(state, mapCanvas) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this._palettes = []; // Array of { name, entries: [...] }
    this._activePaletteIndex = -1;
    this._activeEntryIndex = -1;
    this._container = null;
  }

  /** Get the active palette entry (multi-layer stamp). */
  getActiveEntry() {
    if (this._activePaletteIndex < 0 || this._activeEntryIndex < 0) return null;
    return this._palettes[this._activePaletteIndex]?.entries[this._activeEntryIndex] || null;
  }

  /** Apply a palette entry at the given tile position. */
  applyEntry(entry, tx, ty) {
    if (!entry) return;
    const layers = this.state.get('layers');
    const mapW = this.state.get('mapWidth');
    const mapH = this.state.get('mapHeight');

    if (tx < 0 || ty < 0 || tx >= mapW || ty >= mapH) return;

    for (const [layerName, tileId] of Object.entries(entry.layers || {})) {
      const layerIdx = layers.findIndex(l => l.name === layerName);
      if (layerIdx === -1) continue;
      const layer = layers[layerIdx];
      if (layer.type !== 'tilelayer') continue;
      layer.data[ty * mapW + tx] = tileId;
      this.mapCanvas.invalidateLayer(layerIdx);
    }

    // Apply properties
    if (entry.properties) {
      const activeIdx = this.state.get('activeLayerIndex');
      const layer = layers[activeIdx];
      if (layer) {
        if (!layer.properties) layer.properties = {};
        for (const [propName, value] of Object.entries(entry.properties)) {
          if (!layer.properties[propName]) {
            layer.properties[propName] = { type: 'bool', value: new Array(mapW * mapH).fill(false) };
          }
          layer.properties[propName].value[ty * mapW + tx] = value;
        }
      }
    }

    this.state.set('dirty', true);
  }

  /** Create a new palette. */
  createPalette(name) {
    this._palettes.push({ name, entries: [] });
    this._render();
    return this._palettes.length - 1;
  }

  /** Add an entry to a palette. */
  addEntry(paletteIndex, entry) {
    const palette = this._palettes[paletteIndex];
    if (!palette) return;
    palette.entries.push(entry);
    this._render();
  }

  /** Remove a palette. */
  removePalette(index) {
    this._palettes.splice(index, 1);
    if (this._activePaletteIndex >= this._palettes.length) {
      this._activePaletteIndex = this._palettes.length - 1;
    }
    this._render();
  }

  /** Serialize palettes for saving. */
  toJSON() {
    return this._palettes;
  }

  /** Load palettes from JSON. */
  fromJSON(data) {
    this._palettes = data || [];
    this._render();
  }

  /** Mount UI into a container element. */
  mount(container) {
    this._container = container;
    this._render();
  }

  _render() {
    if (!this._container) return;
    this._container.innerHTML = '';

    const header = el('div', { className: 'panel-header' },
      el('span', {}, 'Custom Palettes'),
      el('div', { className: 'panel-actions' },
        el('button', { title: 'New Palette', onClick: () => {
          const name = prompt('Palette name:');
          if (name) this.createPalette(name);
        }}, '+'),
      ),
    );
    this._container.appendChild(header);

    for (let pi = 0; pi < this._palettes.length; pi++) {
      const palette = this._palettes[pi];
      const isActive = pi === this._activePaletteIndex;

      const section = el('div', { className: `palette-section${isActive ? ' active' : ''}` });
      const sectionHeader = el('div', {
        className: 'palette-section-header',
        onClick: () => { this._activePaletteIndex = pi; this._render(); },
      }, palette.name);
      section.appendChild(sectionHeader);

      if (isActive) {
        for (let ei = 0; ei < palette.entries.length; ei++) {
          const entry = palette.entries[ei];
          const entryEl = el('div', {
            className: `palette-entry${ei === this._activeEntryIndex ? ' active' : ''}`,
            onClick: () => { this._activeEntryIndex = ei; this._render(); },
          }, entry.name || `Entry ${ei + 1}`);
          section.appendChild(entryEl);
        }

        section.appendChild(el('button', {
          className: 'btn btn-sm',
          onClick: () => this._captureEntry(pi),
        }, '+ Capture Current'));
      }

      this._container.appendChild(section);
    }
  }

  /** Capture current selected tile across all layers as a palette entry. */
  _captureEntry(paletteIndex) {
    const name = prompt('Entry name:');
    if (!name) return;

    const layers = this.state.get('layers');
    const selectedTile = this.state.get('selectedTileId');
    const layerMap = {};

    // For now, set selected tile on the active layer
    const activeLayer = this.state.getActiveLayer();
    if (activeLayer) {
      layerMap[activeLayer.name] = selectedTile;
    }

    this.addEntry(paletteIndex, {
      name,
      layers: layerMap,
      properties: {},
    });
  }
}
